import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { MoonIcon } from './Icons';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const isAdmin = user && (user.role === 'ops_admin' || user.role === 'dev_admin');
  const canViewRecordings = user && (user.role === 'counselor' || user.role === 'ops_admin' || user.role === 'dev_admin');
  const canApplyCounselor = user && user.role === 'client';

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-[rgba(8,3,20,0.7)] backdrop-blur-[40px] backdrop-saturate-[180%] border-b border-white/[0.12] shadow-[0_4px_24px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(200,160,255,0.18)] overflow-hidden">
      {/* Liquid Glass shimmer */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-300/25 to-transparent pointer-events-none" aria-hidden />
      <div className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-purple-500/[0.04] to-transparent pointer-events-none" aria-hidden />
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-xl font-bold bg-gradient-to-r from-luna-violet to-luna-pink bg-clip-text text-transparent">
          <MoonIcon size={24} className="text-luna-violet" />
          루나문
        </Link>
        {/* Desktop */}
        <div className="hidden md:flex items-center gap-4 text-sm">
          <Link to="/counselors" className="text-white/70 hover:text-white transition">상담사</Link>
          <Link to="/blog" className="text-white/70 hover:text-white transition">블로그</Link>
          <Link to="/radio" className="text-white/70 hover:text-white transition">라디오</Link>
          <Link to="/voice-rooms" className="text-white/70 hover:text-white transition">보이스룸</Link>
          {user ? (
            <>
              <Link to="/dashboard" className="text-white/70 hover:text-white transition">대시보드</Link>
              <Link to="/chat" className="text-white/70 hover:text-white transition">상담</Link>
              <Link to="/open-chat" className="text-white/70 hover:text-white transition">오픈채팅</Link>
              {canViewRecordings && <Link to="/recordings" className="text-white/70 hover:text-white transition">녹음관리</Link>}
              {canApplyCounselor && <Link to="/counselor-apply" className="text-luna-violet hover:text-luna-violet/80 transition">상담사 신청</Link>}
              {isAdmin && <Link to="/admin" className="text-luna-gold hover:text-luna-gold/80 transition">관리</Link>}
              <NotificationBell />
              <Link to="/profile" className="text-white/70 hover:text-white transition">{user.nickname}</Link>
              <button onClick={handleLogout} className="text-white/50 hover:text-white transition">로그아웃</button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-white/70 hover:text-white transition">로그인</Link>
              <Link to="/register" className="px-4 py-2 rounded-full bg-luna-purple text-white text-sm hover:bg-luna-purple/80 transition">
                회원가입
              </Link>
            </>
          )}
        </div>
        {/* Mobile hamburger */}
        <div className="md:hidden flex items-center gap-3">
          {user && <NotificationBell />}
          <button onClick={() => setMenuOpen(!menuOpen)} className="text-white p-1">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
            </svg>
          </button>
        </div>
      </div>
      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-[rgba(8,3,20,0.96)] backdrop-blur-[48px] backdrop-saturate-[200%] border-b border-white/[0.1] px-6 py-4 space-y-3">
          <Link to="/counselors" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">상담사</Link>
          <Link to="/blog" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">블로그</Link>
          <Link to="/radio" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">라디오</Link>
          <Link to="/voice-rooms" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">보이스룸</Link>
          {user ? (
            <>
              <Link to="/dashboard" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">대시보드</Link>
              <Link to="/chat" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">상담</Link>
              <Link to="/open-chat" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">오픈채팅</Link>
              {canViewRecordings && <Link to="/recordings" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">녹음관리</Link>}
              {canApplyCounselor && <Link to="/counselor-apply" onClick={closeMenu} className="block text-luna-violet hover:text-luna-violet/80 py-2">상담사 신청</Link>}
              {isAdmin && <Link to="/admin" onClick={closeMenu} className="block text-luna-gold hover:text-luna-gold/80 py-2">관리</Link>}
              <Link to="/profile" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">{user.nickname}</Link>
              <button onClick={handleLogout} className="block text-white/50 hover:text-white py-2 w-full text-left">로그아웃</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={closeMenu} className="block text-white/70 hover:text-white py-2">로그인</Link>
              <Link to="/register" onClick={closeMenu} className="block text-luna-purple font-medium py-2">회원가입</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
